import React, { useState } from 'react'
import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { WelcomeTradingAccount } from 'api/Trading-Accounts/Types/WelcomeTradingAccount'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Link } from 'components/Link'
import MoreButton from 'components/MoreButton/MoreButton'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { TableWithExport } from 'utils/table/withExport'

import { LeadWelcomeAccountsActivity } from './LeadWelcomeAccountsActivity'
import { LeadWelcomeAccountsAnalytics } from './LeadWelcomeAccountsAnalytics'

interface LeadWelcomeAccountsTableProps {
  data: PageData<WelcomeTradingAccount[]>
  onActivityOpenModal(data: WelcomeTradingAccount): void
  onDisqualify(data: WelcomeTradingAccount): void
}

export const LeadWelcomeAccountsTableUI: React.FC<LeadWelcomeAccountsTableProps> = (props) => {
  const { data, onActivityOpenModal, onDisqualify } = props

  return (
    <TableWithExport data={data.items}>
      <Grid total={data.totalResults} data={data.items} resizable style={{ zIndex: 0 }}>
        <Column
          minResizableWidth={100}
          width={100}
          title='Name'
          cell={(cellProps: GridCellProps) => (
            <td style={{ color: 'var(--link)', textDecoration: 'underline', width: 'auto' }}>
              <Link
                to={{
                  pathname: generatePath(links.recordsWelcomeAccounts.detailsUrl as string, {
                    id: cellProps.dataItem.id,
                    slug: 'details',
                  }),
                }}
              >
                {cellProps.dataItem.name}
              </Link>
            </td>
          )}
          field='name'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={160}
          width={160}
          title='Account Group'
          field='accountGroup.name'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={160}
          width={160}
          title='Server'
          field='server.name'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={100}
          width={100}
          title='Leverage'
          field='leverage.name'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={80}
          width={80}
          title='Status'
          field='status.name'
          className='table-columns__content'
        />
        <Column
          minResizableWidth={80}
          width={80}
          title='Entity'
          field='tickmillCompany.name'
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => {
            if (!gridCellProps.dataItem?.tickmillCompany?.id) {
              return <td />
            }

            return (
              <td>
                <CompanyTag id={gridCellProps.dataItem.tickmillCompany.id}>
                  {gridCellProps.dataItem.tickmillCompany.name}
                </CompanyTag>
              </td>
            )
          }}
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Created'
          cell={(gridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.created, true)}</td>
          )}
        />
        <Column
          minResizableWidth={120}
          width={120}
          title='Updated'
          cell={(gridCellProps) => (
            <td>{formatLocalDateUTC(gridCellProps.dataItem.updated, true)}</td>
          )}
        />
        <Column
          title='Form'
          children={[
            <Column
              minResizableWidth={120}
              width={120}
              title='Firstname'
              field='form.firstName'
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Lastname'
              field='form.lastName'
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={180}
              width={180}
              title='Email'
              field='form.email'
              className='table-columns__content'
            />,
            <Column
              minResizableWidth={120}
              width={120}
              title='Birthday'
              cell={(gridCellProps) => (
                <td>{formatLocalDateUTC(gridCellProps.dataItem.form.birthday)}</td>
              )}
              className='table-columns__content'
            />,
          ]}
          className='table-columns__content'
        />
        <Column
          title=''
          cell={(gridCellProps: GridCellProps) => (
            <td data-test='more-button-column'>
              <MoreButton
                items={[
                  {
                    name: 'Activity',
                    onClick: () => onActivityOpenModal(gridCellProps.dataItem),
                    'data-test': 'activity-button',
                  },
                  {
                    name: 'Disqualify',
                    onClick: () => onDisqualify(gridCellProps.dataItem),
                    'data-test': 'disqualify-button',
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
          width={50}
        />
      </Grid>
    </TableWithExport>
  )
}

interface OuterProps {
  data: PageData<WelcomeTradingAccount[]>
  onChange(): void
}

export const LeadWelcomeAccountsTable: React.FC<OuterProps> = ({ data, onChange }) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [disqualifyModal, setDisqualifyModal] = useState<{
    data?: WelcomeTradingAccount
    visible: boolean
  }>({ data: undefined, visible: false })
  const [activityModal, setActivityModal] = useState<{
    data?: WelcomeTradingAccount
    visible: boolean
  }>({ data: undefined, visible: false })

  const handleDisqualify = async () => {
    if (disqualifyModal.data?.id) {
      await tradingAccountsApiClient.forceCloseTradesForWelcomeAccount(disqualifyModal.data?.id)
      onChange()
    }
  }

  const handleDisqualifyModalOpen = (welcomeTradingAccount: WelcomeTradingAccount) => {
    setDisqualifyModal({ data: welcomeTradingAccount, visible: true })
  }

  const handleDisqualifyModalClose = () => {
    setDisqualifyModal({ data: undefined, visible: false })
  }

  const handleActivityModalOpen = (welcomeTradingAccount: WelcomeTradingAccount) => {
    setActivityModal({ data: welcomeTradingAccount, visible: true })
  }

  const handleActivityModalClose = () => {
    setActivityModal({ data: undefined, visible: false })
  }

  return (
    <React.Fragment>
      {disqualifyModal.data && (
        <ConfirmationModal
          contentText='Do you want to proceed with disqualifying?'
          yesButtonText='Proceed'
          onYes={handleDisqualify}
          onCancel={handleDisqualifyModalClose}
          visible={disqualifyModal.visible}
        />
      )}
      {activityModal?.data && (
        <Modal width={1500} visible={activityModal.visible} onClose={handleActivityModalClose}>
          <div>
            <LeadWelcomeAccountsActivity data={activityModal.data} />
          </div>
          <div>
            <LeadWelcomeAccountsAnalytics data={activityModal.data} />
          </div>
        </Modal>
      )}
      <LeadWelcomeAccountsTableUI
        data={data}
        onActivityOpenModal={handleActivityModalOpen}
        onDisqualify={handleDisqualifyModalOpen}
      />
    </React.Fragment>
  )
}
